/**
 * Persistence: serialize/load, save-format migrations, and offline progress.
 *
 * The format is versioned JSON, base64-encoded so the export string is one
 * opaque token that survives copy-paste. Every field is validated on load; a
 * corrupt or hand-edited save yields null rather than a half-broken game.
 * Storage is abstracted behind SaveStore so the tests run against memory.
 */

import { ACHIEVEMENTS_BY_ID } from '../data/achievements';
import { DEEP_TIME_CAP_SECONDS } from '../data/museum';
import { DINOS_BY_ID } from '../data/dinos';
import { epsFromState, offlineRate, production } from './economy';
import { BOOST_KINDS, NAME_MAX_LENGTH, addFeat, createInitialState, earnEggs } from './state';
import type { Boost, EraRecord, GameState } from './state';

export const SAVE_KEY = 'dinoclicker.save.v1';
export const CURRENT_VERSION = 11;
export const AUTOSAVE_MS = 30_000;
/** Offline progress is credited for at most this long. */
export const OFFLINE_CAP_SECONDS = 8 * 60 * 60;

/** The cap this save plays under: longer once Deep Time is in the Museum. */
export function offlineCapSeconds(state: GameState): number {
  return state.museum.includes('museum:deep-time') ? DEEP_TIME_CAP_SECONDS : OFFLINE_CAP_SECONDS;
}
/** Below this, offline earnings are granted silently rather than announced. */
export const OFFLINE_NOTICE_SECONDS = 60;

// ---------------------------------------------------------------------------
// Storage

export interface SaveStore {
  get(): string | null;
  set(value: string): void;
  remove(): void;
}

/** localStorage-backed store. Every call is guarded: storage can be disabled or full. */
export function localStorageStore(key: string = SAVE_KEY): SaveStore {
  return {
    get() {
      try {
        return window.localStorage.getItem(key);
      } catch {
        return null;
      }
    },
    set(value) {
      try {
        window.localStorage.setItem(key, value);
      } catch {
        // Quota exceeded or storage disabled; the game keeps running unsaved.
      }
    },
    remove() {
      try {
        window.localStorage.removeItem(key);
      } catch {
        // Nothing to do.
      }
    },
  };
}

export function memoryStore(): SaveStore {
  let value: string | null = null;
  return {
    get: () => value,
    set: (v) => {
      value = v;
    },
    remove: () => {
      value = null;
    },
  };
}

// ---------------------------------------------------------------------------
// Encoding

function toBase64(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}

function fromBase64(encoded: string): string {
  const binary = atob(encoded);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

export function serialize(state: GameState): string {
  return toBase64(JSON.stringify(state));
}

// ---------------------------------------------------------------------------
// Validation

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** A finite, non-negative number, or the fallback. */
function count(v: unknown, fallback = 0): number {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : fallback;
}

function stringList(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}

function countMap(v: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (!isRecord(v)) return out;
  for (const [id, n] of Object.entries(v)) {
    const c = Math.floor(count(n));
    if (c > 0) out[id] = c;
  }
  return out;
}

/** Pet names: non-empty strings, trimmed and cut to the length the input allows. */
/** Past eras, dropping anything malformed. */
function eraLog(v: unknown): EraRecord[] {
  if (!Array.isArray(v)) return [];
  const out: EraRecord[] = [];
  for (const row of v) {
    if (!isRecord(row)) continue;
    out.push({
      era: Math.floor(count(row['era'])),
      eraId: typeof row['eraId'] === 'string' ? row['eraId'] : null,
      expedition: typeof row['expedition'] === 'string' ? row['expedition'] : null,
      seconds: count(row['seconds']),
      eggs: count(row['eggs']),
      fossils: Math.floor(count(row['fossils'])),
      excavated: typeof row['excavated'] === 'string' ? row['excavated'] : null,
      // Absent on every record written before the Museum's history wanted
      // them, which is why they default rather than migrate.
      excavatedName: typeof row['excavatedName'] === 'string' ? row['excavatedName'] : null,
      excavatedSprite: typeof row['excavatedSprite'] === 'string' ? row['excavatedSprite'] : null,
      tiers: Math.floor(count(row['tiers'])),
    });
  }
  return out;
}

/** A map of string lists, dropping anything that is not one. */
function listMap(v: unknown): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  if (!isRecord(v)) return out;
  for (const [k, list] of Object.entries(v)) {
    const clean = [...new Set(stringList(list))];
    if (clean.length > 0) out[k] = clean;
  }
  return out;
}

function nameMap(v: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!isRecord(v)) return out;
  for (const [id, name] of Object.entries(v)) {
    if (typeof name !== 'string') continue;
    const clean = name.trim().slice(0, NAME_MAX_LENGTH);
    if (clean) out[id] = clean;
  }
  return out;
}

/** A boost is kept only if every field is sane; anything else is no boost. */
function boost(v: unknown): Boost | null {
  if (!isRecord(v)) return null;
  const kind = BOOST_KINDS.find((k) => k === v['kind']);
  if (!kind) return null;
  const mult = count(v['mult']);
  const endsAt = count(v['endsAt']);
  if (mult <= 1 || endsAt === 0) return null;
  return { kind, mult, endsAt };
}

/**
 * The species earlier eras hatched, named and evolved (GameState.everHatched
 * and the rest). A save from before they were kept fills them in from what it
 * already shows: the "Meet the..." achievements it holds (each one a species
 * hatched) and its era log's excavations (hatched, named if a name was
 * recorded, evolved if the evolved form was dug up). The current era needs
 * nothing: the achievements read the herd as it stands alongside these.
 */
function everLists(
  raw: Record<string, unknown>,
  achievements: readonly string[],
  log: readonly EraRecord[],
): { everHatched: string[]; everNamed: string[]; everEvolved: string[] } {
  if (Array.isArray(raw['everHatched'])) {
    return {
      everHatched: [...new Set(stringList(raw['everHatched']))],
      everNamed: [...new Set(stringList(raw['everNamed']))],
      everEvolved: [...new Set(stringList(raw['everEvolved']))],
    };
  }
  const hatched = new Set<string>();
  const named = new Set<string>();
  const evolved = new Set<string>();
  for (const id of achievements) {
    if (id.startsWith('first:')) hatched.add(id.slice('first:'.length));
  }
  for (const row of log) {
    const def = row.excavated === null ? undefined : DINOS_BY_ID.get(row.excavated);
    if (!def) continue;
    hatched.add(def.id);
    if (row.excavatedName) named.add(def.id);
    if (row.excavatedSprite === def.evolved.id) evolved.add(def.id);
  }
  return { everHatched: [...hatched], everNamed: [...named], everEvolved: [...evolved] };
}

/** Coerces a decoded object of the current version into a valid GameState. */
function sanitize(raw: Record<string, unknown>, now: number): GameState {
  const fresh = createInitialState(now);
  const totalEggs = count(raw['totalEggs']);
  const era = Math.floor(count(raw['era']));
  // An era's eggs only reset at an extinction, so in era 0 — where none has
  // happened — everything ever laid was laid this era. Stating the invariant
  // here repairs a save that arrived without the field, whatever dropped it.
  const eggsThisEra =
    era === 0 ? Math.max(count(raw['eggsThisEra']), totalEggs) : count(raw['eggsThisEra']);
  // Dropped against the current list: an achievement that has since been
  // removed would otherwise keep paying its EPS bonus and counting toward
  // the meteor unlock, and could push the Stats line past its own total.
  const achievements = [...new Set(stringList(raw['achievements']))].filter((id) =>
    ACHIEVEMENTS_BY_ID.has(id),
  );
  const log = eraLog(raw['eraLog']);
  return {
    version: CURRENT_VERSION,
    eggs: count(raw['eggs']),
    totalEggs,
    clicks: Math.floor(count(raw['clicks'])),
    dinos: countMap(raw['dinos']),
    upgrades: [...new Set(stringList(raw['upgrades']))],
    achievements,
    lastSaved: count(raw['lastSaved'], fresh.lastSaved),
    boost: boost(raw['boost']),
    clickBoost: boost(raw['clickBoost']),
    cracks: Math.floor(count(raw['cracks'])),
    names: nameMap(raw['names']),
    shards: count(raw['shards']),
    shardsEarned: count(raw['shardsEarned']),
    meteorsBroken: Math.floor(count(raw['meteorsBroken'])),
    meteorKindsBroken: countMap(raw['meteorKindsBroken']),
    // A repeatable tile is listed once per purchase, so duplicates are kept.
    shop: stringList(raw['shop']),
    inventory: countMap(raw['inventory']),
    evolved: [...new Set(stringList(raw['evolved']))],
    fossils: Math.floor(count(raw['fossils'])),
    fossilsEarned: Math.floor(count(raw['fossilsEarned'])),
    era,
    eggsThisEra,
    museum: [...new Set(stringList(raw['museum']))],
    eraId: typeof raw['eraId'] === 'string' ? raw['eraId'] : null,
    nameBook: nameMap(raw['nameBook']),
    eraStartedAt: count(raw['eraStartedAt'], fresh.eraStartedAt),
    expedition: typeof raw['expedition'] === 'string' ? raw['expedition'] : null,
    eraOffer: stringList(raw['eraOffer']),
    eraLog: log,
    shared: raw['shared'] === true,
    bonusesCaught: countMap(raw['bonusesCaught']),
    feats: [...new Set(stringList(raw['feats']))],
    bossRest: countMap(raw['bossRest']),
    bossWins: listMap(raw['bossWins']),
    bossTrophies: [...new Set(stringList(raw['bossTrophies']))],
    ...everLists(raw, achievements, log),
  };
}

// ---------------------------------------------------------------------------
// Migrations

/** Maps a save at version N to version N + 1. Add an entry when bumping CURRENT_VERSION. */
export type Migration = (raw: Record<string, unknown>) => Record<string, unknown>;
export type Migrations = Readonly<Record<number, Migration>>;

const MIGRATIONS: Migrations = {
  // v1 → v2 (M5): falling-egg boost and crack counter.
  1: (raw) => ({ ...raw, boost: null, cracks: 0 }),
  // v2 → v3: pet names.
  2: (raw) => ({ ...raw, names: {} }),
  // v3 → v4: meteor shards and the shard shop.
  3: (raw) => ({
    ...raw,
    shards: 0,
    shardsEarned: 0,
    meteorsBroken: 0,
    meteorKindsBroken: {},
    shop: [],
  }),
  // v4 → v5: a second boost slot for click boosts.
  4: (raw) => ({ ...raw, clickBoost: null }),
  // v5 → v6: the inventory and dinosaur evolutions.
  5: (raw) => ({ ...raw, inventory: {}, evolved: [] }),
  // v6 → v7: Fossil Eras. An existing save becomes era 0 with no fossils,
  // and everything it has laid so far counts as this era's — it has not
  // had an extinction to reset it.
  6: (raw) => ({
    ...raw,
    fossils: 0,
    fossilsEarned: 0,
    era: 0,
    eggsThisEra: typeof raw['totalEggs'] === 'number' ? raw['totalEggs'] : 0,
    museum: [],
  }),
  // v7 → v8: era modifiers and the name book. An era already under way
  // stays plain, which is what the first era is anyway.
  7: (raw) => ({ ...raw, eraId: null, nameBook: {} }),
  // v8 → v9: the era log. An era already running has no recorded start, so
  // it is taken to have begun at the last save rather than at the epoch.
  8: (raw) => ({
    ...raw,
    eraStartedAt: typeof raw['lastSaved'] === 'number' ? raw['lastSaved'] : 0,
    expedition: null,
    eraLog: [],
  }),
  // v9 → v10: amber became an excavation. Only the era log's keys change, and
  // only for rows that recorded one — the dinosaur, its name and its sprite
  // are the same three facts under new names.
  9: (raw) => ({
    ...raw,
    eraLog: Array.isArray(raw['eraLog'])
      ? raw['eraLog'].map((row) => {
          if (!isRecord(row)) return row;
          const { amber, amberName, amberSprite, ...rest } = row;
          return {
            ...rest,
            excavated: amber ?? null,
            excavatedName: amberName ?? null,
            excavatedSprite: amberSprite ?? null,
          };
        })
      : [],
  }),
  // v10 → v11: tier 15 became Baryonyx (evolving into Suchomimus) and the
  // Argentinosaurus moved to the Giant era's resident. Every saved
  // Argentinosaurus was the tier-15 dinosaur, so it becomes a Baryonyx: its
  // level, name, evolution, upgrades, achievements, boss record and fossils.
  10: (raw) => renameTier(raw, TIER_15_RENAME),
};

/** Tier 15's old ids and the ones that replaced them (v10 → v11). */
const TIER_15_RENAME = {
  tier: ['argentinosaurus', 'baryonyx'],
  evolved: ['patagotitan', 'suchomimus'],
} as const;

/**
 * A save with one tier's ids swapped for another's, wherever a save holds a
 * dinosaur id: keys of the per-dinosaur maps, the lists of tier ids, the
 * dinosaur's upgrade and achievement ids, and the era log's fossils.
 */
function renameTier(
  raw: Record<string, unknown>,
  { tier: [from, to], evolved: [fromEvolved, toEvolved] }: typeof TIER_15_RENAME,
): Record<string, unknown> {
  const id = (v: unknown): unknown => (v === from ? to : v);
  const keys = (v: unknown): unknown =>
    isRecord(v) ? Object.fromEntries(Object.entries(v).map(([k, x]) => [id(k), x])) : v;
  const list = (v: unknown): unknown => (Array.isArray(v) ? v.map(id) : v);
  // 'first:argentinosaurus', 'level:argentinosaurus:50', 'dino:argentinosaurus:1'.
  const part = (v: unknown): unknown =>
    typeof v === 'string'
      ? v
          .split(':')
          .map((p) => (p === from ? to : p))
          .join(':')
      : v;
  const parts = (v: unknown): unknown => (Array.isArray(v) ? v.map(part) : v);
  const sprite = (v: unknown): unknown => (v === fromEvolved ? toEvolved : id(v));
  const out: Record<string, unknown> = { ...raw };
  for (const k of ['dinos', 'names', 'nameBook', 'bossRest', 'bossWins']) out[k] = keys(raw[k]);
  for (const k of ['evolved', 'everHatched', 'everNamed', 'everEvolved']) out[k] = list(raw[k]);
  for (const k of ['achievements', 'upgrades']) out[k] = parts(raw[k]);
  if (Array.isArray(raw['eraLog'])) {
    out['eraLog'] = raw['eraLog'].map((row) =>
      isRecord(row)
        ? {
            ...row,
            excavated: id(row['excavated']),
            excavatedSprite: sprite(row['excavatedSprite']),
          }
        : row,
    );
  }
  return out;
}

/**
 * Walks a decoded save forward through migrations until it reaches the
 * current version. Returns null for a save from the future or one with no
 * migration path, since either would be guesswork.
 */
export function migrate(
  raw: unknown,
  migrations: Migrations = MIGRATIONS,
): Record<string, unknown> | null {
  if (!isRecord(raw)) return null;
  let version = raw['version'];
  if (typeof version !== 'number' || !Number.isInteger(version)) return null;
  if (version > CURRENT_VERSION) return null;

  let current = raw;
  while (version < CURRENT_VERSION) {
    const step = migrations[version];
    if (!step) return null;
    current = { ...step(current), version: version + 1 };
    version += 1;
  }
  return current;
}

/** Decodes an export string. Null for anything that is not a usable save. */
export function deserialize(
  encoded: string,
  now: number = Date.now(),
  migrations: Migrations = MIGRATIONS,
): GameState | null {
  let raw: unknown;
  try {
    raw = JSON.parse(fromBase64(encoded.trim()));
  } catch {
    return null;
  }
  const migrated = migrate(raw, migrations);
  return migrated ? sanitize(migrated, now) : null;
}

// ---------------------------------------------------------------------------
// Save / load

export function save(state: GameState, store: SaveStore, now: number = Date.now()): void {
  state.lastSaved = now;
  store.set(serialize(state));
}

/**
 * Save while the welcome-back panel still holds eggs: the stored copy has them
 * paid, the running game does not. Every save moves `lastSaved` on, so without
 * this a tab killed in the background (a phone browser does it silently, with
 * no pagehide) reloaded to a save that had already counted the time away and
 * never paid it. The panel still pays the live game on Collect.
 */
export function saveWithPending(
  state: GameState,
  store: SaveStore,
  pending: OfflineResult | null,
  now: number = Date.now(),
): void {
  state.lastSaved = now;
  const copy = pending && pending.eggs > 0 ? deserialize(serialize(state), now) : null;
  if (!copy || !pending) {
    store.set(serialize(state));
    return;
  }
  collectOffline(copy, { ...pending });
  store.set(serialize(copy));
}

export function load(store: SaveStore, now: number = Date.now()): GameState | null {
  const encoded = store.get();
  return encoded ? deserialize(encoded, now) : null;
}

// ---------------------------------------------------------------------------
// Offline progress

export interface OfflineResult {
  /** Seconds credited, after the cap. */
  seconds: number;
  /** True when the time away exceeded the cap. */
  capped: boolean;
  /** The cap, in seconds: 8 hours, or 24 with Deep Time. */
  cap: number;
  /** Fraction of normal production that was credited. */
  rate: number;
  eggs: number;
  /** Whether the welcome-back panel should mention it. */
  notable: boolean;
}

/**
 * Credits production for the time since the last save at the offline rate
 * (a fraction of unboosted EPS), capped. Pure apart from mutating the state
 * it is handed.
 */
/**
 * What the time away was worth, with `lastSaved` moved on so the same span
 * cannot pay twice. It does **not** credit the eggs. The welcome-back panel
 * has a Collect button, and paying here put them in the total before the
 * player pressed it, which read as the button doing nothing. Pay with
 * `collectOffline` — at once when nothing is shown, on Collect when it is.
 */
export function offlineProgress(state: GameState, now: number = Date.now()): OfflineResult {
  const elapsed = Math.max(0, (now - state.lastSaved) / 1000);
  const cap = offlineCapSeconds(state);
  const capped = elapsed > cap;
  const seconds = capped ? cap : elapsed;
  const rate = offlineRate(state);
  const eggs = production(epsFromState(state, 1) * rate, seconds);

  state.lastSaved = now;

  return {
    seconds,
    capped,
    cap,
    rate,
    eggs,
    notable: seconds >= OFFLINE_NOTICE_SECONDS && eggs > 0,
  };
}

/**
 * Two spells away that have not been paid yet, as one. A second measurement
 * can arrive while the panel for the first is still open — the tab becoming
 * visible measures again — and the two have to become one offer rather than
 * one of them being paid out early.
 */
export function mergeOffline(a: OfflineResult, b: OfflineResult): OfflineResult {
  return {
    seconds: a.seconds + b.seconds,
    capped: a.capped || b.capped,
    cap: b.cap,
    // The rate is a property of the state, not of the span, so the later
    // reading is the current one.
    rate: b.rate,
    eggs: a.eggs + b.eggs,
    notable: a.notable || b.notable,
  };
}

/**
 * Pays what `offlineProgress` measured. Safe to call twice on the same
 * result — the second call is worth nothing — so the panel's Collect and the
 * unload safety net cannot both credit it.
 */
export function collectOffline(state: GameState, result: OfflineResult): void {
  if (result.eggs <= 0) return;
  if (result.capped) addFeat(state, 'long-nap');
  earnEggs(state, result.eggs);
  result.eggs = 0;
}
